import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import {Helmet} from 'react-helmet';
import {useNavigate, useParams} from 'react-router';

import classNames from 'classnames';
import {capitalize} from 'es-toolkit';
import {reaction, toJS} from 'mobx';

import type {GoalApiModel as Goal} from '@yourcoach/shared/api/goal';
import type {ProgramExpandedCommon as ProgramT} from '@yourcoach/shared/api/program';
import type {RpcRequest} from '@yourcoach/shared/api/utils/apiRequest';

import {useI18n} from '@yourcoach/i18n';
import {programExpandCommon, programStore} from '@yourcoach/shared/api/program';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {apiRequest} from '@yourcoach/shared/api/utils/apiRequest';
import {createCollectionStore} from '@yourcoach/shared/api/utils/collectionStore';
import ArrowBackIcon from '@yourcoach/shared/assets/icons/arrow-back.svg';
import ArrowNextIcon from '@yourcoach/shared/assets/icons/arrow-next.svg';
import {alert} from '@yourcoach/shared/components/alert';
import {
  FULL_PORTION,
  isProgramDirty,
} from '@yourcoach/shared/utils/entity/program';
import {logger} from '@yourcoach/shared/utils/logger';
import {deepClone} from '@yourcoach/shared/utils/object';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import Button from '@src/components/Button';
import Loader from '@src/components/Loader/Loader';
import useIsVisible from '@src/hooks/useIsVisible';
import {useLeaveBlocker} from '@src/hooks/useLeaveBlocker';
import LogoOnlyLayout from '@src/layouts/LogoOnly';
import {routerPath} from '@src/routes/path';

import type {Tab} from './useTabs';

import useTabs from './useTabs';
import createProgramFlow from './utils/createProgram';
import updateProgramFlow from './utils/updateProgram';

import styles from './styles.module.css';

const NO_PROGRAM_ID = 'program:000000000000000000000000';

export type {ProgramT};

const CRUProgramPage: FC = () => {
  const {t} = useI18n();

  const {clonedProgramId, programId} = useParams<{
    clonedProgramId?: string;
    programId?: string;
  }>();

  const navigate = useNavigate();

  const [overlayIsVisible, showOverlay, hideOverlay] = useIsVisible();

  const [isFetching, setIsFetching] = useState(
    !!(programId || clonedProgramId),
  );
  const [program, setProgram] = useState<null | ProgramT>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const tabIndexRef = useRef(tabIndex);
  const programRef = useRef<ProgramT>(undefined);
  const originalProgram = useRef<ProgramT>(undefined);

  const goalsStore = useRef(
    createCollectionStore<Goal>({
      method: 'coach.goals.unbound.list',
      params: {
        query: [
          ['course_id', '==', null],
          ['program_id', '==', clonedProgramId || programId || NO_PROGRAM_ID],
        ],
        sort: [['sort_order', 1]],
      },
    }),
  ).current;

  const originalGoals = useRef<Goal[]>([]);

  const {refs, tabs} = useTabs(program, tabIndex, goalsStore);

  const isCreatingMode = useMemo(() => !programId, [programId]);
  const programHasEditions = useMemo(
    () =>
      program &&
      program._id &&
      !!(program && program.editions && program.editions.length),
    [program],
  );

  const isCreatingUI = useMemo(
    () => isCreatingMode || !programHasEditions,
    [isCreatingMode, programHasEditions],
  );

  const fetchProgram = useCallback(async () => {
    try {
      if (!programId && !clonedProgramId) {
        return;
      }

      setIsFetching(true);

      let fetchedProgram = (await programStore.coach.fetch({
        _id: programId || clonedProgramId,
        expand: programExpandCommon,
      })) as ProgramT;

      const coaches: ProgramT['coaches'] = {};

      /**
       * old programs were created with lower case role "coach"
       * but the backend creates coaches with "Coach" role
       * it causes that we block leave from the page because "coach" !== "Coach"
       */
      Object.keys(fetchedProgram.coaches).forEach(userId => {
        coaches[userId] = {
          ...fetchedProgram.coaches[userId],
          role: capitalize(fetchedProgram.coaches[userId].role),
        };
      });

      fetchedProgram.coaches = coaches;

      if (clonedProgramId) {
        fetchedProgram = {
          ...fetchedProgram,
          _id: undefined,
          coach_ids: [currentUserStore.user!._id],
          coaches: {
            [currentUserStore.user!._id]: {
              portion: FULL_PORTION,
              resigned: null,
              role: 'Coach',
              sign: null,
              signed: null,
            },
          },
          expanded_coaches: [currentUserStore.user!],
          revision: undefined,
          title: `[CLONE] ${fetchedProgram.title}`,
        } as unknown as ProgramT;
      }

      setTabIndex(0);
      setProgram(fetchedProgram);

      originalProgram.current = JSON.parse(
        JSON.stringify(fetchedProgram),
      ) as ProgramT;

      programRef.current = fetchedProgram;

      setIsFetching(false);
    } catch (error) {
      logger.error(error);

      alert.show({
        buttons: [
          {
            label: t('shared:ok'),
            onClick: () => {
              navigate(-1);
            },
          },
        ],
        message: error.message,
        title: t('shared:message.error'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clonedProgramId, history, programId, programStore.coach]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  useEffect(() => {
    const dispose = reaction(
      () => goalsStore.isLoaded,
      isLoaded => {
        if (isLoaded) {
          originalGoals.current = deepClone(goalsStore.items.slice());

          dispose();
        }
      },
    );

    return () => {
      dispose();

      goalsStore.clear();
    };
  }, [goalsStore]);

  useEffect(() => {
    tabIndexRef.current = tabIndex;
  }, [tabIndex]);

  useEffect(() => {
    logger.event(
      isCreatingMode
        ? 'create_program_screen_view'
        : 'edit_program_screen_view',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProgramDataFromCurrentTab = useCallback(
    async (direction?: 'back' | 'next') => {
      const currentTab = tabs[tabIndexRef.current]!;

      if (!refs[currentTab.key].current) {
        return false;
      }

      const tabProgramData =
        await refs[currentTab.key].current!.getData(direction);

      if (!tabProgramData) {
        return false;
      }

      const newProgram = {
        ...programRef.current,
        ...tabProgramData,
      } as ProgramT;

      programRef.current = newProgram;

      setProgram(newProgram);

      return true;
    },
    [refs, tabs],
  );

  const hasUnsavedChanges = useCallback(async () => {
    await getProgramDataFromCurrentTab();

    const isProgramChanged = isProgramDirty(
      programRef.current,
      originalProgram.current,
    );

    const areGoalsChanged = !isEqual(
      goalsStore.items.slice(),
      originalGoals.current.slice(),
    );

    return isProgramChanged || areGoalsChanged;
  }, [getProgramDataFromCurrentTab, goalsStore.items]);

  const {setShouldBlockLeave} = useLeaveBlocker({
    message: t('shared:unsaved_changes_will_be_lost'),
  });

  useEffect(() => {
    // TODO: Find a better way to check for unsaved changes
    const interval = setInterval(async () => {
      const result = await hasUnsavedChanges();

      setShouldBlockLeave(result);
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jumpTo = useCallback(
    async (index: number) => {
      if (index < 0 || index > tabs.length - 1) {
        return;
      }

      setTabIndex(index);
    },
    [tabs.length],
  );

  const updateGoalsIfNeeded = useCallback(
    async (forProgramId: string) => {
      const goals = goalsStore.items.slice();

      const batch: RpcRequest[] = [];

      (clonedProgramId ? [] : originalGoals.current).forEach(goal => {
        const index = goals.findIndex(item => item._id === goal._id);

        if (index >= 0) {
          if (!isEqual(toJS(goal), toJS(goals[index]))) {
            const goalToUpdate = goals[index]!;

            batch.push({
              method: 'coach.goals.unbound.update',
              params: {
                _id: goalToUpdate._id,
                description: goalToUpdate.description,
                is_client_managed: goalToUpdate.is_client_managed,
                title: goalToUpdate.title,
              },
            });
          }

          goals.splice(index, 1);
        } else {
          batch.push({
            method: 'coach.goals.unbound.delete',
            params: {
              _id: goal._id,
            },
          });
        }
      });

      if (goals.length) {
        const maxSortOrder = Math.max(
          ...goalsStore.items
            .slice()
            .map(goal => (goal as Goal).sort_order || 0),
        );

        goals.forEach((goal, i) => {
          const order = i * 10;
          const _order = (i + 1) * 10;

          batch.push({
            method: 'coach.goals.unbound.create',
            params: {
              description: goal.description,
              is_client_managed: goal.is_client_managed,
              parent_goal_id: goal._id.includes('goal') ? goal._id : null,
              program_id: forProgramId,
              sort_order: maxSortOrder ? maxSortOrder + _order : order,
              title: goal.title,
            },
          });
        });
      }

      if (batch.length) {
        await apiRequest({batch});
      }
    },
    [clonedProgramId, goalsStore.items],
  );

  const createProgram = useCallback(async () => {
    try {
      showOverlay();

      const createdProgram = await createProgramFlow(programRef.current!);

      await updateGoalsIfNeeded(createdProgram._id);

      hideOverlay();

      logger.event('program_create_success');

      // Prevent leave blocking
      originalProgram.current = programRef.current;
      originalGoals.current = goalsStore.items.slice();
      setShouldBlockLeave(false);

      const path = routerPath.coachPractice.program.$buildPath({
        params: {
          practiceSlugOrId:
            currentUserStore.user!.slug || currentUserStore.user!._id,
          programSlugOrId: createdProgram.slug || createdProgram._id,
        },
      });

      navigate(path);
    } catch (error) {
      hideOverlay();

      logger.error(error);

      alert.error(error);
    }
  }, [
    showOverlay,
    updateGoalsIfNeeded,
    hideOverlay,
    goalsStore.items,
    setShouldBlockLeave,
    navigate,
  ]);

  const updateProgram = useCallback(async () => {
    try {
      const updatedProgram = await updateProgramFlow(
        originalProgram.current!,
        programRef.current!,
      );

      await updateGoalsIfNeeded(updatedProgram._id);

      // Prevent leave blocking
      originalProgram.current = programRef.current;
      originalGoals.current = goalsStore.items.slice();
      setShouldBlockLeave(false);

      logger.event('program_edit_success');

      hideOverlay();

      const practiceSlug = currentUserStore.user!.slug;
      const practiceId = currentUserStore.user!._id;
      const programSlug = updatedProgram!.slug;

      const programId = updatedProgram!._id;

      const path = routerPath.coachPractice.program.$buildPath({
        params: {
          practiceSlugOrId: practiceSlug || practiceId,
          programSlugOrId: programSlug || programId,
        },
      });

      navigate(path);
    } catch (error) {
      hideOverlay();

      if (error.code === 'isnt_provider_coaches') {
        alert.show({
          buttons: [
            {
              label: t(
                'shared:CRUProgram.isnt_provider_coaches_popup.success_button',
              ),
              onClick: () => {
                const completeTabIndex = tabs.findIndex(
                  tab => tab.key === 'complete',
                );

                setTimeout(() => {
                  jumpTo(completeTabIndex);
                });
              },
              scheme: 'primary',
            },
            {
              label: t(
                'shared:CRUProgram.isnt_provider_coaches_popup.cancel_button',
              ),
            },
          ],
          message: t(
            'shared:CRUProgram.isnt_provider_coaches_popup.description',
            {
              coaches: programRef
                .current!.expanded_coaches.filter(coach => !coach.is_provider)
                .map(coach => coach.name)
                .join(', '),
            },
          ),
          title: t('shared:CRUProgram.isnt_provider_coaches_popup.header'),
        });

        return;
      }

      logger.error(error);

      alert.error(error);
    }
  }, [
    updateGoalsIfNeeded,
    goalsStore.items,
    setShouldBlockLeave,
    hideOverlay,
    navigate,
    tabs,
    jumpTo,
    t,
  ]);

  const onMainNextButtonClick = useCallback(async () => {
    if (!(await getProgramDataFromCurrentTab('next'))) {
      return;
    }

    const currentTab = tabs[tabIndex]!;

    switch (currentTab.key) {
      case 'description':
        logger.event('program_name_continue_tap');
        break;
      case 'details': {
        const lastEdition = programRef.current?.editions.slice().pop();

        logger.event('program_type_continue_tap', {
          type_selected: lastEdition?.group_size === 1 ? 'individual' : 'group',
        });
        break;
      }
      case 'goals':
        logger.event('main_goal_continue_tap', {
          screen: 'program',
        });
        break;
      case 'library':
        logger.event('library_continue_tap');
        break;
      case 'schedule':
        logger.event('schedule_continue_tap');
        break;
      default:
        break;
    }

    if (isCreatingUI) {
      if (tabIndex === tabs.length - 1) {
        if (!isCreatingMode) {
          logger.event('edit_program_tap');
          updateProgram();
        } else {
          logger.event('create_program_tap');
          createProgram();
        }

        return;
      }
    } else {
      updateProgram();

      return;
    }

    jumpTo(tabIndex + 1);
  }, [
    getProgramDataFromCurrentTab,
    tabs,
    tabIndex,
    isCreatingUI,
    jumpTo,
    isCreatingMode,
    updateProgram,
    createProgram,
  ]);

  const onNextButtonClick = useCallback(async () => {
    if (!(await getProgramDataFromCurrentTab('next'))) {
      return;
    }

    jumpTo(tabIndex + 1);
  }, [getProgramDataFromCurrentTab, jumpTo, tabIndex]);

  const onPrevButtonClick = useCallback(async () => {
    if (!(await getProgramDataFromCurrentTab('back'))) {
      return;
    }

    jumpTo(tabIndex - 1);
  }, [getProgramDataFromCurrentTab, jumpTo, tabIndex]);

  const onTabClick = useCallback(
    async (tab: Tab) => {
      const clickedTabIndex = tabs.findIndex(item => item.key === tab.key);

      if (
        !(await getProgramDataFromCurrentTab(
          clickedTabIndex > tabIndex ? 'next' : 'back',
        ))
      ) {
        return;
      }

      jumpTo(clickedTabIndex);
    },
    [getProgramDataFromCurrentTab, jumpTo, tabIndex, tabs],
  );

  return (
    <LogoOnlyLayout
      contentClassName={styles.container}
      v2
      withBackButton={false}
    >
      <Helmet
        title={
          /**
           * @i18n_parser
           *
           * t('shared:CRUProgram.title.create')
           * t('shared:CRUProgram.title.update')
           */
          t(`shared:CRUProgram.title.${isCreatingMode ? 'create' : 'update'}`)
        }
      />
      <h1 className={styles.title}>
        {
          /**
           * @i18n_parser
           *
           * t('shared:CRUProgram.title.create')
           * t('shared:CRUProgram.title.update')
           */
          t(`shared:CRUProgram.title.${isCreatingMode ? 'create' : 'update'}`)
        }
      </h1>
      {isFetching ? (
        <div className={styles.loadingSpinnerContainer}>
          <Loader />
          <p className={styles.text}>
            {t('shared:CRUProgram.loading_message')}
          </p>
        </div>
      ) : (
        <>
          <nav className={styles.tabs}>
            {tabs.map((tab, i, itself) => {
              const isActive = i <= tabIndex;
              const isCurrent = i === tabIndex;
              const isLast = i === itself.length - 1;

              return (
                <div
                  className={classNames(
                    styles.tab,
                    isActive && styles.active,
                    !isCurrent && styles.notCurrent,
                    isLast && styles.last,
                    !isCreatingUI && styles.clickable,
                  )}
                  key={tab.key}
                  onClick={!isCreatingUI ? () => onTabClick(tab) : undefined}
                >
                  {tab.title}
                </div>
              );
            })}
          </nav>
          <section className={styles.content}>
            {tabs[tabIndex]!.content}
          </section>
          <footer className={styles.footer}>
            {tabIndex > 0 ? (
              <Button
                className={classNames(styles.button)}
                data-test-id={TestID.create_program.button.back}
                onClick={onPrevButtonClick}
              >
                <ArrowBackIcon />
                {t('shared:CRUProgram.back_button')}
              </Button>
            ) : null}
            <Button
              className={classNames(styles.button, styles.main)}
              data-test-id={TestID.create_program.button.continue}
              onClick={onMainNextButtonClick}
            >
              {
                /**
                 * @i18n_parser
                 *
                 * t('shared:CRUProgram.main_next_button.create')
                 * t('shared:CRUProgram.main_next_button.update')
                 * t('shared:CRUProgram.main_next_button.create_last')
                 */
                t(
                  `shared:CRUProgram.main_next_button.${
                    isCreatingUI
                      ? tabIndex === tabs.length - 1 && !isCreatingMode
                        ? 'update'
                        : `create${tabIndex === tabs.length - 1 ? '_last' : ''}`
                      : 'update'
                  }`,
                )
              }
              {tabIndex !== tabs.length - 1 && isCreatingMode ? (
                <ArrowNextIcon />
              ) : null}
            </Button>
            {!isCreatingUI && tabIndex < tabs.length - 1 ? (
              <Button
                className={classNames(styles.button)}
                onClick={onNextButtonClick}
              >
                {t('shared:CRUProgram.next_button')}
                <ArrowNextIcon />
              </Button>
            ) : null}
          </footer>
          {overlayIsVisible ? (
            <div className="overlay">
              <Loader />
            </div>
          ) : null}
        </>
      )}
    </LogoOnlyLayout>
  );
};

export default memo(CRUProgramPage);
